import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  featured_image_url: string | null;
  amazon_asin: string | null;
  amazon_link: string | null;
  product_name: string | null;
  product_price: number | null;
  product_rating: number | null;
  product_images: string[];
  price_label: string | null;
  post_type: string;
  status: string;
  seo_title: string | null;
  seo_description: string | null;
  faq_data: { question: string; answer: string }[];
  pros: string[];
  cons: string[];
  author_name: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  tags?: { id: string; name: string; slug: string; parent_id?: string | null }[];
}

export interface AmazonProduct {
  asin: string;
  title: string;
  imageUrl: string;
  price: string | null;
  priceValue: number | null;
  rating: number | null;
  features: string[];
  detailUrl: string;
  affiliateLink: string;
}

// Colunas necessárias para o card da listagem — evita baixar `content`, `faq_data`, etc.
const LIST_COLUMNS =
  "id, slug, title, excerpt, featured_image_url, product_rating, product_name, price_label, post_type, status, author_name, published_at, created_at, updated_at";

const PUBLISHED_PAGE_SIZE = 24;

async function hydrateTags<T extends { id: string }>(rows: T[]) {
  if (rows.length === 0) return rows.map((r) => ({ ...r, tags: [] }));
  const postIds = rows.map((r) => r.id);
  const { data: links } = await supabase
    .from("blog_post_tags")
    .select("blog_post_id, blog_tag_id, blog_tags(id, name, slug, parent_id)")
    .in("blog_post_id", postIds);
  const map = new Map<string, any[]>();
  for (const link of (links || []) as any[]) {
    const tag = link.blog_tags;
    if (!tag) continue;
    const arr = map.get(link.blog_post_id) ?? [];
    arr.push(tag);
    map.set(link.blog_post_id, arr);
  }
  return rows.map((r) => ({ ...r, tags: map.get(r.id) ?? [] }));
}

export function usePublishedPosts(
  search?: string,
  tagSlugs?: string[] | string,
  page: number = 1,
  pageSize: number = PUBLISHED_PAGE_SIZE,
) {
  const slugList = Array.isArray(tagSlugs)
    ? tagSlugs
    : tagSlugs
      ? [tagSlugs]
      : undefined;

  return useQuery({
    queryKey: [
      "blog-posts",
      "published",
      search ?? null,
      slugList?.join(",") ?? null,
      page,
      pageSize,
    ],
    queryFn: async () => {
      // Filtro de categoria server-side: slugs -> tag_ids -> post_ids
      let allowedPostIds: string[] | null = null;
      if (slugList && slugList.length > 0) {
        const { data: tagRows } = await supabase
          .from("blog_tags")
          .select("id")
          .in("slug", slugList);
        const tagIds = (tagRows || []).map((t: any) => t.id);
        if (tagIds.length === 0) {
          return { posts: [] as BlogPost[], totalCount: 0, totalPages: 0 };
        }
        const { data: linkRows } = await supabase
          .from("blog_post_tags")
          .select("blog_post_id")
          .in("blog_tag_id", tagIds);
        allowedPostIds = Array.from(
          new Set((linkRows || []).map((l: any) => l.blog_post_id)),
        );
        if (allowedPostIds.length === 0) {
          return { posts: [] as BlogPost[], totalCount: 0, totalPages: 0 };
        }
      }

      const currentPage = Math.max(1, page);
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from("blog_posts")
        .select(LIST_COLUMNS, { count: "exact" })
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .range(from, to);

      if (search) {
        query = query.or(`title.ilike.%${search}%,excerpt.ilike.%${search}%`);
      }
      if (allowedPostIds) {
        query = query.in("id", allowedPostIds);
      }

      const { data, error, count } = await query;
      if (error) throw error;

      const rows = (data || []) as any[];
      const withTags = await hydrateTags(rows);
      const totalCount = count ?? 0;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

      return {
        posts: withTags as unknown as BlogPost[],
        totalCount,
        totalPages,
      };
    },
    placeholderData: (prev) => prev,
  });
}

/**
 * Opções compartilhadas: hook no cliente e loader da rota no servidor usam a
 * mesma chave, então o artigo já chega renderizado no HTML.
 */
export const postBySlugQueryOptions = (slug: string | undefined) => ({
  queryKey: ["blog-post", slug] as const,
  queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const { data: tagData } = await supabase
        .from("blog_post_tags")
        .select("blog_tag_id")
        .eq("blog_post_id", data.id);

      let tags: { id: string; name: string; slug: string }[] = [];
      if (tagData && tagData.length > 0) {
        const tagIds = tagData.map((t) => t.blog_tag_id);
        const { data: tagsResult } = await supabase
          .from("blog_tags")
          .select("*")
          .in("id", tagIds);
        tags = tagsResult || [];
      }

    return { ...data, tags } as unknown as BlogPost;
  },
});

export function usePostBySlug(slug: string | undefined) {
  return useQuery({
    ...postBySlugQueryOptions(slug),
    enabled: !!slug,
  });
}

export function useAdminPosts(
  statusFilter?: string,
  search?: string,
  page: number = 1,
  pageSize: number = 10,
  categoryTagId?: string | null,
) {
  return useQuery({
    queryKey: ["blog-posts", "admin", statusFilter, search, page, pageSize, categoryTagId],
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize; // fetch 1 extra to detect hasMore

      let query = supabase
        .from("blog_posts")
        .select("*")
        .order("created_at", { ascending: false })
        .range(from, to);

      if (statusFilter && statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      if (search) {
        query = query.ilike("title", `%${search}%`);
      }

      if (categoryTagId) {
        const { data: tagged, error: tagErr } = await supabase
          .from("blog_post_tags")
          .select("blog_post_id")
          .eq("blog_tag_id", categoryTagId);
        if (tagErr) throw tagErr;
        const postIds = (tagged || []).map((t) => t.blog_post_id);
        if (postIds.length === 0) {
          return { posts: [], hasMore: false };
        }
        query = query.in("id", postIds);
      }

      const { data, error } = await query;
      if (error) throw error;
      const rows = (data || []) as unknown as BlogPost[];
      const hasMore = rows.length > pageSize;
      const sliced = rows.slice(0, pageSize);

      // Fetch tags for each post
      const postsWithTags = await Promise.all(
        sliced.map(async (post) => {
          const { data: tagData } = await supabase
            .from("blog_post_tags")
            .select("blog_tag_id")
            .eq("blog_post_id", post.id);
          if (tagData && tagData.length > 0) {
            const tagIds = tagData.map((t) => t.blog_tag_id);
            const { data: tags } = await supabase
              .from("blog_tags")
              .select("id, name, slug, parent_id")
              .in("id", tagIds);
            return { ...post, tags: tags || [] } as unknown as BlogPost;
          }
          return { ...post, tags: [] } as unknown as BlogPost;
        }),
      );

      return { posts: postsWithTags, hasMore };
    },
  });
}

export function useBlogTags() {
  return useQuery({
    queryKey: ["blog-tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_tags")
        .select("*")
        .order("display_order", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });
}

export function useUpdatePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<BlogPost>;
    }) => {
      const { data, error } = await supabase
        .from("blog_posts")
        .update(updates as any)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
    },
  });
}

export function useDeletePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("blog_posts")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
    },
  });
}

export function useUpdatePostCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      postId,
      newSubcategoryId,
    }: {
      postId: string;
      newSubcategoryId: string | null;
    }) => {
      // Buscar tags atuais do post
      const { data: postTags, error: ptErr } = await supabase
        .from("blog_post_tags")
        .select("blog_tag_id")
        .eq("blog_post_id", postId);
      if (ptErr) throw ptErr;

      const tagIds = (postTags || []).map((t) => t.blog_tag_id);

      // Identificar quais são subcategorias (parent_id != null)
      let subcatIds: string[] = [];
      if (tagIds.length > 0) {
        const { data: tagsInfo, error: tiErr } = await supabase
          .from("blog_tags")
          .select("id, parent_id")
          .in("id", tagIds);
        if (tiErr) throw tiErr;
        subcatIds = (tagsInfo || [])
          .filter((t) => t.parent_id !== null)
          .map((t) => t.id);
      }

      // Remover associações de subcategoria existentes
      if (subcatIds.length > 0) {
        const { error: delErr } = await supabase
          .from("blog_post_tags")
          .delete()
          .eq("blog_post_id", postId)
          .in("blog_tag_id", subcatIds);
        if (delErr) throw delErr;
      }

      // Inserir nova subcategoria, se informada
      if (newSubcategoryId) {
        const { error: insErr } = await supabase
          .from("blog_post_tags")
          .insert({ blog_post_id: postId, blog_tag_id: newSubcategoryId });
        if (insErr) throw insErr;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
    },
  });
}

export function useSearchBestsellers() {
  return useMutation({
    mutationFn: async ({
      keywords,
      searchIndex,
    }: {
      keywords?: string;
      searchIndex?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke(
        "amazon-bestsellers",
        { body: { keywords, searchIndex } }
      );
      if (error) throw error;
      return data as { items: AmazonProduct[]; totalResults: number };
    },
  });
}

export function useGenerateReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productData,
      customPrompt,
    }: {
      productData: AmazonProduct;
      customPrompt?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke(
        "generate-review",
        { body: { productData, customPrompt } }
      );
      if (error) throw error;
      return data as { post: BlogPost };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
    },
  });
}
